import { forwardRef } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { SortIcon } from './table/SortIcon';
import type {
  TableProps,
  TableHeaderProps,
  TableBodyProps,
  TableRowProps,
  TableHeadProps,
  TableCellProps,
} from './table/tableTypes';

// Oeffentlicher Einstiegspunkt: Seitenblaetterung und Typen werden hier
// weiterexportiert, damit der Importpfad `@/components/ui` unveraendert bleibt.
export { Pagination } from './table/Pagination';
export type {
  ColumnDef,
  TableProps,
  TableHeaderProps,
  TableBodyProps,
  TableRowProps,
  TableHeadProps,
  TableCellProps,
  PaginationProps,
} from './table/tableTypes';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * TableHeader component
 */
export const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ children, className, ...props }, ref) => (
    <thead ref={ref} className={cn('border-b border-white/[0.08]', className)} {...props}>
      {children}
    </thead>
  )
);

TableHeader.displayName = 'TableHeader';

/**
 * TableBody component
 */
export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ children, className, ...props }, ref) => (
    <tbody ref={ref} className={cn('divide-y divide-white/[0.06]', className)} {...props}>
      {children}
    </tbody>
  )
);

TableBody.displayName = 'TableBody';

/**
 * TableRow component
 */
export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ children, selected, className, ...props }, ref) => (
    <motion.tr
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={cn('transition-colors duration-150', selected && 'bg-primary/10', className)}
      {...(props as Omit<HTMLMotionProps<'tr'>, 'ref'>)}
    >
      {children}
    </motion.tr>
  )
);

TableRow.displayName = 'TableRow';

/**
 * TableHead (th) component
 */
export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ children, sortable, sortDirection, onSort, className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'px-4 py-3',
        'text-left text-sm font-semibold text-surface-700',
        'bg-white/[0.02]',
        sortable && 'cursor-pointer select-none hover:bg-white/[0.05] transition-colors',
        className
      )}
      onClick={sortable ? onSort : undefined}
      aria-sort={
        sortDirection === 'asc'
          ? 'ascending'
          : sortDirection === 'desc'
          ? 'descending'
          : undefined
      }
      {...props}
    >
      <span className="inline-flex items-center">
        {children}
        {sortable && <SortIcon direction={sortDirection ?? null} />}
      </span>
    </th>
  )
);

TableHead.displayName = 'TableHead';

/**
 * TableCell (td) component
 */
export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ children, className, ...props }, ref) => (
    <td ref={ref} className={cn('px-4 py-3 text-sm text-surface-800', className)} {...props}>
      {children}
    </td>
  )
);

TableCell.displayName = 'TableCell';

/**
 * Table component with Liquid Glass design
 *
 * @example
 * ```tsx
 * <Table hoverable striped>
 *   <TableHeader>
 *     <TableRow>
 *       <TableHead sortable sortDirection="asc">Name</TableHead>
 *       <TableHead>Status</TableHead>
 *       <TableHead>Date</TableHead>
 *     </TableRow>
 *   </TableHeader>
 *   <TableBody>
 *     <TableRow>
 *       <TableCell>John Doe</TableCell>
 *       <TableCell><Badge variant="success">Active</Badge></TableCell>
 *       <TableCell>2024-01-15</TableCell>
 *     </TableRow>
 *   </TableBody>
 * </Table>
 * <Pagination currentPage={1} totalPages={10} onPageChange={(p) => setPage(p)} />
 * ```
 */
export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ children, fixed, striped, hoverable, className, ...props }, ref) => (
    <div
      className={cn(
        'w-full overflow-hidden',
        'backdrop-blur-xl',
        'bg-white/[0.06]',
        'border border-white/[0.10]',
        'rounded-xl',
        'shadow-xl shadow-black/20'
      )}
    >
      <div className="overflow-x-auto">
        <table
          ref={ref}
          className={cn(
            'w-full',
            fixed && 'table-fixed',
            striped && '[&_tbody_tr:nth-child(odd)]:bg-white/[0.02]',
            hoverable && '[&_tbody_tr]:hover:bg-white/[0.06] [&_tbody_tr]:transition-colors',
            className
          )}
          {...props}
        >
          {children}
        </table>
      </div>
    </div>
  )
);

Table.displayName = 'Table';

export default Table;
